// src/modules/app-views/products/seed.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import aiService from './service';
import { useAppStore } from 'stores/app';
import seedGoodsReceiptParameters from 'modules/app-views/goodsReceipt/seed';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const DEFAULT_SEEDS = {
  units: [
    {
      uid: generateUidV4(),
      name: 'UN',
      description: 'Unidade',
    },
  ],
  sku: {
    autoSku: 'alpha6',
  },
};

// Tier configuration for UI display
const TIER_CONFIG = {
  economy: {
    badge: 'economical',
    badgeColor: 'green',
    stars: 3,
  },
  balanced: {
    badge: 'balanced',
    badgeColor: 'blue',
    stars: 4,
  },
  premium: {
    badge: 'premium',
    badgeColor: 'orange',
    stars: 5,
  },
};

// *******************************************************************************
// MAIN FUNCTION
// *******************************************************************************
async function seedProductsParameters({ moduleId, storedParameters }) {
  if (!moduleId) return;

  const moduleParams = storedParameters?.modules?.[moduleId];
  const results = [];

  // Seed units if none exist
  const unitsResult = await seedCategory({
    moduleId,
    category: 'units',
    currentData: moduleParams?.units,
    defaultData: DEFAULT_SEEDS.units,
  });
  if (unitsResult) results.push(unitsResult);

  // Seed SKU settings if none exist
  const skuResult = await seedSku({
    moduleId,
    currentData: moduleParams?.sku,
    defaultData: DEFAULT_SEEDS.sku,
  });
  if (skuResult) results.push(skuResult);

  // Seed default warehouse (warehouses now stored under products parameters)
  const warehouseResults = await seedGoodsReceiptParameters({
    moduleId: 'products',
    storedParameters,
  });
  if (warehouseResults?.length) results.push(...warehouseResults);

  // Fetch and store AI prices (always refresh on module load)
  const aiPricesResult = await fetchAndStoreAiPrices({ moduleId });
  if (aiPricesResult) results.push(aiPricesResult);

  return results;
}

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default seedProductsParameters;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function checkHasActiveItems(dataArray) {
  if (!dataArray || !Array.isArray(dataArray)) return false;
  return dataArray.some(item => !item.removed);
}

async function seedCategory({ moduleId, category, currentData, defaultData }) {
  const hasActiveItems = checkHasActiveItems(currentData);

  if (hasActiveItems) {
    return null; // Already has data, skip seeding
  }

  try {
    await appService.updateModulePreferences({
      category,
      moduleId,
      data: defaultData,
    });

    return { category, seeded: true, data: defaultData };
  } catch (error) {
    console.error(`[seedProductsParameters] Error seeding ${category}:`, error);
    return { category, seeded: false, error };
  }
}

async function seedSku({ moduleId, currentData, defaultData }) {
  // Skip if SKU settings already exist
  if (currentData?.autoSku) {
    return null;
  }

  try {
    await appService.updateModulePreferences({
      category: 'sku',
      moduleId,
      data: defaultData,
    });

    return { category: 'sku', seeded: true, data: defaultData };
  } catch (error) {
    console.error('[seedProductsParameters] Error seeding sku:', error);
    return { category: 'sku', seeded: false, error };
  }
}

/**
 * Fetch AI prices from API and store in Zustand state
 * This data is cached locally (not persisted to backend)
 * Only fetches if not already loaded (prevents duplicate requests on page navigation)
 */
async function fetchAndStoreAiPrices({ moduleId }) {
  try {
    // Check if aiPrices already exist in store (skip fetch on page navigation within module)
    const currentParams = useAppStore.getState().parameters;
    const existingAiPrices = currentParams?.modules?.[moduleId]?.aiPrices;

    if (existingAiPrices?.length > 0) {
      return null; // Already loaded, skip fetch
    }

    const response = await aiService.getAiPrices();

    // Extract models from Jarvisly standard response format
    // Response format: { success, data: { success, models } }
    const models = extractModelsFromResponse(response);

    if (!models?.length) {
      console.warn('[seedProductsParameters] Failed to fetch AI prices:', response);
      return null;
    }

    // Transform API response to add UI config
    const modelsWithConfig = models.map((model, index) => ({
      ...model,
      // Add tier-based UI config
      ...TIER_CONFIG[model.tier],
      // Mark gemini model as recommended (aligned with services module)
      recommended: model.alias === 'gemini',
    }));

    // Update Zustand store directly (not persisted to backend)
    const storeParams = useAppStore.getState().parameters || { application: {}, modules: {} };
    const updatedParams = {
      ...storeParams,
      modules: {
        ...storeParams.modules,
        [moduleId]: {
          ...storeParams.modules?.[moduleId],
          aiPrices: modelsWithConfig,
        },
      },
    };

    useAppStore.getState().setParameters(updatedParams);

    return { category: 'aiPrices', seeded: true, data: modelsWithConfig };
  } catch (error) {
    console.error('[seedProductsParameters] Error fetching AI prices:', error);
    return { category: 'aiPrices', seeded: false, error };
  }
}

/**
 * Extract models array from various possible response structures
 * Handles Jarvisly standard format: { success, data: { success, models } }
 */
function extractModelsFromResponse(response) {
  if (!response) return null;

  // Direct models array (already extracted by axiosService)
  if (response.models && Array.isArray(response.models)) {
    return response.models;
  }

  // Nested in data (standard Jarvisly format)
  if (response.data?.models && Array.isArray(response.data.models)) {
    return response.data.models;
  }

  // Double nested (if axiosService returns full response)
  if (response.data?.data?.models && Array.isArray(response.data.data.models)) {
    return response.data.data.models;
  }

  return null;
}
